import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectStat, selectPlayer, selectHeatmap } from '../../slices/gameStatsSlice';
import {
  summarizeShots,
  summarizeGoals,
  summarizeKicks,
  summarizePasses,
  summarizeDirectionalPasses,
  summarizeSimpleList,
  summarizeSaves,
  summarizeDuels,
  summarizeRebounds,
  summarizeClearances,
  summarizeClearanceRecoveries,
  summarizeGoalkeeperTicks,
  buildAdvancedPlayerStats,
  extractCompletedPasses,
  extractDangerousTurnovers,
  extractPlayerXg,
  extractTurnoverTouches,
  resolvePlayerName,
  toFiniteNumber
} from '../../utils/gameStatsHelpers';

const COUNTRY_OVERRIDES = {
  UK: 'GB',
  GB: 'GB',
  ENGLAND: 'GB',
  SCOTLAND: 'GB',
  WALES: 'GB',
  USA: 'US',
  UNITEDSTATES: 'US',
  'UNITED STATES': 'US',
  US: 'US',
  UAE: 'AE',
  RUS: 'RU',
  KSA: 'SA',
  KOREA: 'KR',
  SOUTHKOREA: 'KR',
  NORTHKOREA: 'KP',
  VIETNAM: 'VN',
  HONGKONG: 'HK',
  TAIWAN: 'TW',
  MACAU: 'MO',
  CZECH: 'CZ'
};

const REGIONAL_FLAG_REGEX = /[\u{1F1E6}-\u{1F1FF}]{2}/u;

const resolveCountry = (value) => {
  if (typeof value !== 'string') return null;
  if (REGIONAL_FLAG_REGEX.test(value)) {
    const match = value.match(REGIONAL_FLAG_REGEX);
    return match ? { emoji: match[0], code: null } : null;
  }
  const trimmed = value.trim();
  if (!trimmed) return null;
  const upper = trimmed.toUpperCase();
  const override = COUNTRY_OVERRIDES[upper];
  const normalized = override || upper;
  const letters = (normalized.match(/[A-Z]/g) || []).slice(0, 2);
  if (letters.length < 2) return null;
  const [a, b] = letters;
  const base = 0x1F1E6;
  const offset = (ch) => {
    const code = ch.charCodeAt(0) - 65;
    return code >= 0 && code < 26 ? code : null;
  };
  const oa = offset(a);
  const ob = offset(b);
  if (oa === null || ob === null) return null;
  const emoji = String.fromCodePoint(base + oa, base + ob);
  const code = `${a}${b}`;
  return { emoji, code };
};

const normalizeKey = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.toLowerCase();
};

const findMapEntry = (map, key) => {
  if (!map || typeof map.entries !== 'function') return undefined;
  if (map.has(key)) return map.get(key);
  const normalized = normalizeKey(key);
  if (!normalized) return undefined;
  for (const [entryKey, value] of map.entries()) {
    if (typeof entryKey === 'string' && normalizeKey(entryKey) === normalized) {
      return value;
    }
  }
  return undefined;
};

const getCountLoose = (map, key) => {
  const entry = findMapEntry(map, key);
  if (Number.isFinite(entry)) return Number(entry);
  if (entry && typeof entry === 'object') {
    if (Number.isFinite(entry.total)) return Number(entry.total);
    if (Number.isFinite(entry.count)) return Number(entry.count);
  }
  return 0;
};

const COLUMN_DEFS = [
  { key: 'player', label: 'Player', align: 'left', statIndex: 0, sortable: false },
  { key: 'goals', label: 'G', align: 'center', statIndex: 2, heatmapKey: 'Goals', tooltip: 'Goals scored' },
  { key: 'assists', label: 'A', align: 'center', statIndex: 3, heatmapKey: 'Assists', tooltip: 'Assists delivered' },
  { key: 'kicks', label: 'Kicks', align: 'center', statIndex: 4, heatmapKey: 'Kicks', tooltip: 'Total kicks' },
  { key: 'passesCompleted', label: 'Passes', align: 'center', statIndex: 5, heatmapKey: 'Passes', tooltip: 'Completed passes' },
  {
    key: 'passAccuracy',
    label: 'Acc',
    align: 'center',
    statIndex: 6,
    tooltip: 'Pass accuracy',
    sortValue: (row) => (Number.isFinite(row.passAccuracyPct) ? row.passAccuracyPct : -1)
  },
  { key: 'saves', label: 'Saves', align: 'center', statIndex: 7, heatmapKey: 'Saves', tooltip: 'Shots stopped' },
  {
    key: 'xg',
    label: 'xG',
    align: 'center',
    statIndex: 8,
    heatmapKey: 'xG',
    tooltip: 'Expected goals',
    sortValue: (row) => row.xg
  },
  { key: 'shotsOnTarget', label: 'Shots On', align: 'center', statIndex: 9, heatmapKey: 'Shots', tooltip: 'Shots on target' },
  { key: 'shotsOffTarget', label: 'Shots Off', align: 'center', statIndex: 12, heatmapKey: 'Shots Off', tooltip: 'Shots off target' },
  { key: 'bumps', label: 'Bumps', align: 'center', statIndex: 10, heatmapKey: 'Bumps', tooltip: 'Bumps (space mode)' },
  { key: 'touches', label: 'Touches', align: 'center', statIndex: 11, heatmapKey: 'Touches', tooltip: 'Touches (space mode)' }
];

const EMPTY_MATCH = {};
const EMPTY_TEAM = [];

function PlayerStats() {
  const matches = useSelector((state) => state.gameStats.matches) || [];
  const mtc = useSelector((state) => state.gameStats.selectedMatch) || 0;
  const selectedStat = useSelector((state) => state.gameStats.selectedStat);
  const dispatch = useDispatch();

  const currentMatch = matches[mtc] || EMPTY_MATCH;
  const redTeam = useMemo(() => (Array.isArray(currentMatch.redTeam) ? currentMatch.redTeam : EMPTY_TEAM), [currentMatch.redTeam]);
  const blueTeam = useMemo(() => (Array.isArray(currentMatch.blueTeam) ? currentMatch.blueTeam : EMPTY_TEAM), [currentMatch.blueTeam]);
  const isSpaceMode = Boolean(currentMatch.spaceMode);

  const rows = useMemo(() => {
    const players = Array.isArray(currentMatch.player) ? currentMatch.player : [];
    const goals = Array.isArray(currentMatch.goals) ? currentMatch.goals : [];
    const kicks = Array.isArray(currentMatch.kicks) ? currentMatch.kicks : [];
    const passes = Array.isArray(currentMatch.passes) ? currentMatch.passes : [];
    const shots = Array.isArray(currentMatch.shots) ? currentMatch.shots : [];
    const saves = Array.isArray(currentMatch.saves) ? currentMatch.saves : [];
    const bumps = Array.isArray(currentMatch.bumps) ? currentMatch.bumps : [];
    const touches = Array.isArray(currentMatch.touches) ? currentMatch.touches : [];
    const duels = Array.isArray(currentMatch.duels) ? currentMatch.duels : [];
    const reboundsList = Array.isArray(currentMatch.rebounds) ? currentMatch.rebounds : [];
    const clearancesList = Array.isArray(currentMatch.clearances) ? currentMatch.clearances : [];
    const clearanceRecoveriesList = Array.isArray(currentMatch.clearanceRecoveries) ? currentMatch.clearanceRecoveries : [];
    const gkList = Array.isArray(currentMatch.gkOccupancy) ? currentMatch.gkOccupancy : [];

    const advancedStats = buildAdvancedPlayerStats(currentMatch) || {};
    const advancedMap = advancedStats.advanced instanceof Map ? advancedStats.advanced : new Map();

    const { goals: goalMap, assists: assistMap } = summarizeGoals(goals);
    const kicksMap = summarizeKicks(kicks);
    const passMap = summarizePasses(passes);
    const directionalPassMap = summarizeDirectionalPasses(passes);
    const { byShooter: shotMap, savedBy: shotSavedBy } = summarizeShots(shots, {
      stadium: currentMatch.stadium,
      redTeam,
      blueTeam
    });
    const savesMap = summarizeSaves(saves, shotSavedBy);
    const bumpsMap = summarizeSimpleList(bumps);
    const touchesMap = summarizeSimpleList(touches);
    const duelsMap = summarizeDuels(duels);
    const reboundsMap = summarizeRebounds(reboundsList);
    const clearancesMap = summarizeClearances(clearancesList);
    const clearanceRecoveriesMap = summarizeClearanceRecoveries(clearanceRecoveriesList);
    const gkMap = summarizeGoalkeeperTicks(gkList);
    const analysisPlayers = Array.isArray(currentMatch.players) ? currentMatch.players : [];
    const analysisMap = new Map();
    analysisPlayers.forEach((entry) => {
      if (!entry) return;
      const mappedNick = entry.nick || entry.player || entry.name || resolvePlayerName(entry);
      if (!mappedNick) return;
      const metrics = entry.metrics && typeof entry.metrics === 'object' ? entry.metrics : entry;
      analysisMap.set(mappedNick, metrics);
    });

    const redSet = new Set(redTeam || []);
    const blueSet = new Set(blueTeam || []);

    return players.reduce((acc, player) => {
      if (!player) return acc;
      const nick = player.nick || player.player || player.name || resolvePlayerName(player);
      if (!nick) return acc;

      const analysisMetrics = findMapEntry(analysisMap, nick) || {};
      const advancedMetrics = findMapEntry(advancedMap, nick) || {};
      const playedMinutes = toFiniteNumber(
        player.minutesPlayed ||
        player.minutes ||
        player.timeOnPitch ||
        player.timePlayed ||
        analysisMetrics.minutes ||
        analysisMetrics.minutesPlayed
      );
      const hasMinutes = Number.isFinite(playedMinutes) && playedMinutes > 0;
      const hasTrackedStats =
        getCountLoose(goalMap, nick) > 0 ||
        getCountLoose(assistMap, nick) > 0 ||
        getCountLoose(passMap, nick) > 0 ||
        getCountLoose(savesMap, nick) > 0 ||
        Boolean(findMapEntry(shotMap, nick)) ||
        getCountLoose(kicksMap, nick) > 0 ||
        getCountLoose(touchesMap, nick) > 0 ||
        getCountLoose(bumpsMap, nick) > 0 ||
        Boolean(findMapEntry(duelsMap, nick)) ||
        Boolean(findMapEntry(reboundsMap, nick)) ||
        Boolean(findMapEntry(clearancesMap, nick)) ||
        Boolean(findMapEntry(clearanceRecoveriesMap, nick)) ||
        Boolean(findMapEntry(gkMap, nick));
      const isTeamPlayer = redSet.has(nick) || blueSet.has(nick);
      if (!hasMinutes && !hasTrackedStats && !isTeamPlayer) {
        return acc;
      }

      const nation = player.nation || player.country || player.flag || analysisMetrics.nation || null;
      const countryData = resolveCountry(nation);
      const flagEmoji = countryData?.emoji || '🏳️';
      const flagCode = countryData?.code || null;
      const flagUrl = flagCode ? `https://flagcdn.com/24x18/${flagCode.toLowerCase()}.png` : null;
      const shotInfo = findMapEntry(shotMap, nick) || { total: 0, onTarget: 0, xg: 0, xgOnTarget: 0 };

      const metricGoals = toFiniteNumber(player.metrics?.goals) || 0;
      const metricAssists = toFiniteNumber(player.metrics?.assists) || 0;
      const metricKicks = toFiniteNumber(player.metrics?.kicks) || 0;
      const goalsCount = Math.max(getCountLoose(goalMap, nick), metricGoals);
      const assistsCount = Math.max(getCountLoose(assistMap, nick), metricAssists);
      const kicksCount = Math.max(getCountLoose(kicksMap, nick), metricKicks);
      const passesCompletedMetric = toFiniteNumber(analysisMetrics.passesCompleted) || 0;
      const passesCompleted = passesCompletedMetric > 0
        ? passesCompletedMetric
        : Math.max(
            getCountLoose(passMap, nick),
            extractCompletedPasses(player, 0),
            toFiniteNumber(player.metrics?.passesCompleted) || 0,
            toFiniteNumber(advancedMetrics.passesCompleted) || 0,
            toFiniteNumber(player.passesCompleted) || 0
          );
      const directionalCompleted = findMapEntry(directionalPassMap, nick) || { forward: 0, lateral: 0, backward: 0 };
      const savesCount = Math.max(
        getCountLoose(savesMap, nick),
        toFiniteNumber(player.metrics?.saves) || 0,
        toFiniteNumber(player.saves) || 0,
        toFiniteNumber(player.goalkeeping?.saves) || 0,
        toFiniteNumber(analysisMetrics.saves) || 0
      );
      const shotsOnTarget = Math.max(
        shotInfo.onTarget || 0,
        toFiniteNumber(player.targetedShots) || 0,
        toFiniteNumber(player.shotsOnTarget) || 0,
        toFiniteNumber(analysisMetrics.shotsOnTarget) || 0
      );
      const rawOffTarget = Number.isFinite(shotInfo.offTarget)
        ? shotInfo.offTarget
        : ((shotInfo.total || 0) - (shotInfo.onTarget || 0));
      const shotsOffTarget = Math.max(
        rawOffTarget || 0,
        toFiniteNumber(player.shotsOffTarget) || 0,
        toFiniteNumber(player.shotsWide) || 0,
        toFiniteNumber(player.missedShots) || 0,
        toFiniteNumber(analysisMetrics.shotsOffTarget) || 0
      );
      const bumpsCount = Math.max(
        getCountLoose(bumpsMap, nick),
        toFiniteNumber(player.bumps) || 0,
        toFiniteNumber(player.contacts) || 0,
        toFiniteNumber(analysisMetrics.bumps) || 0
      );
      const touchesCount = Math.max(
        getCountLoose(touchesMap, nick),
        toFiniteNumber(player.touches) || 0,
        toFiniteNumber(player.totalTouches) || 0,
        toFiniteNumber(player.touchCount) || 0,
        toFiniteNumber(analysisMetrics.touches) || 0
      );
      const duelsEntry = findMapEntry(duelsMap, nick) || { total: 0 };
      const reboundsEntry = findMapEntry(reboundsMap, nick) || { total: 0, clearanceRecoveries: 0 };
      const clearancesEntry = findMapEntry(clearancesMap, nick) || { total: 0 };
      const clearanceRecoveryEntry = findMapEntry(clearanceRecoveriesMap, nick) || { total: 0 };
      const gkEntry = findMapEntry(gkMap, nick) || { ticks: 0 };

      const xgFromShots = shotInfo.xg || 0;
      const playerXg = extractPlayerXg(player, 0);
      const metricsXg = toFiniteNumber(analysisMetrics.xg) || 0;
      const xgTotal = metricsXg > 0 ? metricsXg : (playerXg > 0 ? playerXg : xgFromShots);

      const turnoverTouches = Math.max(
        extractTurnoverTouches(analysisMetrics, 0),
        extractTurnoverTouches(player, 0),
        extractTurnoverTouches(advancedMetrics, 0)
      );
      const dangerousTouches = Math.max(
        extractDangerousTurnovers(analysisMetrics, 0),
        extractDangerousTurnovers(player, 0),
        extractDangerousTurnovers(advancedMetrics, 0)
      );
      const passAttempts = Math.max(0, passesCompleted + turnoverTouches + dangerousTouches);
      const passAccuracyPct = passAttempts > 0 ? (passesCompleted / passAttempts) * 100 : null;
      const passAccuracyLabel = passAccuracyPct !== null ? `${passAccuracyPct.toFixed(1)}%` : '—';

      acc.push({
        nick,
        nation,
        flag: flagEmoji,
        flagCode,
        flagUrl,
        team: redSet.has(nick) ? 'red' : (blueSet.has(nick) ? 'blue' : 'spec'),
        goals: Math.round(goalsCount) || 0,
        assists: Math.round(assistsCount) || 0,
        kicks: Math.round(kicksCount) || 0,
        passesCompleted: Math.round(passesCompleted) || 0,
        passAttempts,
        passAccuracy: passAccuracyLabel,
        passAccuracyPct,
        passesForwardCompleted: Number(directionalCompleted.forward || 0),
        passesLateralCompleted: Number(directionalCompleted.lateral || 0),
        passesBackwardCompleted: Number(directionalCompleted.backward || 0),
        duelsTotal: Number(duelsEntry.total || 0),
        duelsCorner: Number(duelsEntry.corner || 0),
        duelsInterception: Number(duelsEntry.interception || 0),
        duelsWon: Number(duelsEntry.wins || 0),
        duelsLost: Number(duelsEntry.losses || 0),
        reboundsTotal: Number(reboundsEntry.total || 0),
        reboundClearanceRecoveries: Number(reboundsEntry.clearanceRecoveries || 0),
        clearancesTotal: Number(clearancesEntry.total || 0),
        clearanceRecoveries: Number(clearanceRecoveryEntry.total || 0),
        gkTicks: Number(gkEntry.ticks || 0),
        saves: Math.round(savesCount) || 0,
        xg: Number.isFinite(xgTotal) ? Number(xgTotal) : 0,
        shotsOnTarget: Math.round(shotsOnTarget) || 0,
        shotsOffTarget: Math.round(shotsOffTarget) || 0,
        bumps: Math.round(bumpsCount) || 0,
        touches: Math.round(touchesCount) || 0
      });

      return acc;
    }, []);
  }, [currentMatch, redTeam, blueTeam]);

  const sortedRows = useMemo(() => {
    if (!Array.isArray(rows)) return [];

    if (selectedStat <= 0) {
      const redList = [];
      const blueList = [];
      const others = [];
      rows.forEach((row) => {
        if (redTeam.includes(row.nick)) redList.push(row);
        else if (blueTeam.includes(row.nick)) blueList.push(row);
        else others.push(row);
      });
      return [...redList, ...blueList, ...others];
    }

    const column = COLUMN_DEFS.find((def) => def.statIndex === selectedStat);
    if (!column) return rows;

    const getValue = column.sortValue
      ? column.sortValue
      : (row) => {
          const value = row[column.key];
          if (column.key === 'passAccuracy') {
            return Number.isFinite(row.passAccuracyPct) ? row.passAccuracyPct : 0;
          }
          return Number.isFinite(value) ? value : parseFloat(String(value).replace('%', '')) || 0;
        };

    const teamFiltered = rows.filter((row) => redTeam.includes(row.nick) || blueTeam.includes(row.nick));
    const sorted = [...teamFiltered].sort((a, b) => (getValue(b) || 0) - (getValue(a) || 0));
    return sorted;
  }, [rows, redTeam, blueTeam, selectedStat]);

  const handleHeaderSelect = (column) => {
    if (!column || typeof column.statIndex !== 'number') return;
    dispatch(selectStat(column.statIndex));
  };

  const handlePlayerHover = (event) => {
    const playerName = event.currentTarget.dataset.player;
    if (playerName) {
      dispatch(selectHeatmap('Heatmap'));
      dispatch(selectPlayer(playerName));
    }
  };

  const handlePlayerClick = (playerName) => {
    if (!playerName) return;
    dispatch(selectPlayer(playerName));
    dispatch(selectHeatmap('Heatmap'));
  };

  const handleStatClick = (event) => {
    const playerName = event.currentTarget.dataset.player;
    const statKey = event.currentTarget.dataset.stat;
    if (playerName && statKey) {
      dispatch(selectPlayer(playerName));
      dispatch(selectHeatmap(statKey));
    }
  };

  const activeStatIndex = selectedStat;

  const visibleColumns = useMemo(() => {
    if (isSpaceMode) return COLUMN_DEFS;
    return COLUMN_DEFS.filter((column) => column.key !== 'bumps' && column.key !== 'touches');
  }, [isSpaceMode]);

  const renderCellValue = (row, column) => {
    if (column.key === 'player') return row.nick;
    if (column.key === 'xg') return row.xg.toFixed(2);
    if (column.key === 'passAccuracy') return row.passAccuracy;
    const value = row[column.key];
    if (value === null || value === undefined) return '—';
    return value;
  };

  return (
    <>
      <thead className="gs-pro-player-head">
        <tr>
          {visibleColumns.map((column) => {
            const isSortable = column.sortable !== false && typeof column.statIndex === 'number';
            const isActive = isSortable && activeStatIndex === column.statIndex;
            const classNames = ['gs-pro-th'];
            if (column.align === 'center') classNames.push('is-center');
            if (isSortable) classNames.push('is-sortable');
            if (isActive) classNames.push('is-active');
            return (
              <th
                key={column.key}
                className={classNames.join(' ')}
                scope="col"
                title={column.tooltip}
                aria-sort={isSortable ? (isActive ? 'descending' : 'none') : undefined}
              >
                {isSortable ? (
                  <button
                    type="button"
                    className="gs-pro-th-button"
                    onClick={() => handleHeaderSelect(column)}
                  >
                    <span className="gs-pro-th-label">{column.label}</span>
                    <span className={`gs-pro-sort-indicator${isActive ? ' active' : ''}`}>{isActive ? '▼' : '▾'}</span>
                  </button>
                ) : (
                  <span className="gs-pro-th-label">{column.label}</span>
                )}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((row, index) => {
          const rowKey = `${row.nick}-${index}`;
          const rowClass = ['gs-pro-player-row'];
          if (row.team === 'red') rowClass.push('red');
          else if (row.team === 'blue') rowClass.push('blue');
          else rowClass.push('neutral');

          return (
            <tr key={rowKey} className={rowClass.join(' ')}>
              {visibleColumns.map((column) => {
                const isActiveColumn = column.statIndex === activeStatIndex && column.sortable !== false;
                const commonClassNames = ['gs-pro-td'];
                if (column.align === 'center') commonClassNames.push('is-center');
                if (isActiveColumn) commonClassNames.push('is-highlight');

                if (column.key === 'player') {
                  return (
                    <td
                      key={`${rowKey}-${column.key}`}
                      className={`${commonClassNames.join(' ')} gs-pro-playercell`}
                      data-player={row.nick}
                      onMouseEnter={handlePlayerHover}
                      onFocus={handlePlayerHover}
                      onClick={() => handlePlayerClick(row.nick)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handlePlayerClick(row.nick);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <span className={`gs-pro-team-dot ${row.team}`} aria-hidden="true" />
                      <span className="gs-pro-player-flag" aria-hidden="true">
                        {row.flagUrl ? (
                          <img
                            src={row.flagUrl}
                            alt={`${row.flagCode || row.nation || 'Unknown'} flag`}
                            loading="lazy"
                            width={24}
                            height={18}
                          />
                        ) : (
                          row.flag
                        )}
                      </span>
                      <span className="gs-pro-player-meta">
                        <span className="gs-pro-playername">{row.nick}</span>
                      </span>
                    </td>
                  );
                }

                const value = renderCellValue(row, column);
                const heatmapKey = column.heatmapKey;
                const isClickable = Boolean(heatmapKey);

                const cellProps = {
                  key: `${rowKey}-${column.key}`,
                  className: commonClassNames.join(' '),
                  title: column.key === 'passAccuracy' && row.passAttempts > 0
                    ? `${row.passAccuracy} • ${row.passesCompleted}/${row.passAttempts} completed`
                    : undefined
                };

                if (isClickable) {
                  Object.assign(cellProps, {
                    'data-player': row.nick,
                    'data-stat': heatmapKey,
                    onClick: handleStatClick,
                    onKeyDown: (event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleStatClick(event);
                      }
                    },
                    role: 'button',
                    tabIndex: 0
                  });
                }

                return (
                  <td {...cellProps}>
                    <span className="gs-pro-stat-value">{value}</span>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </>
  );
}

export default PlayerStats;
